import { uiText } from '../../i18n';

const ALLOWED_TYPES = new Set(['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/ogg', 'audio/webm']);
const TYPE_BY_EXTENSION: Record<string, string> = {
  mp3: 'audio/mpeg', wav: 'audio/wav', m4a: 'audio/mp4', mp4: 'audio/mp4',
  ogg: 'audio/ogg', webm: 'audio/webm',
};

export const MIN_SAMPLE_MS = 2_000;
export const MAX_SAMPLE_MS = 90_000;
export const MAX_SAMPLE_BYTES = 20 * 1024 * 1024;

/** MIME уточняется только по поддерживаемому расширению; окончательно байты проверяет сервер. */
export function confirmAudioType(file: File): File {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  const browserType = file.type.split(';')[0].trim().toLowerCase();
  const alias = browserType === 'audio/x-wav' || browserType === 'audio/wave' ? 'audio/wav' : browserType === 'audio/x-m4a' ? 'audio/mp4' : browserType === 'audio/mp3' ? 'audio/mpeg' : browserType;
  const type = ALLOWED_TYPES.has(alias) ? alias : (!alias || alias === 'application/octet-stream') ? TYPE_BY_EXTENSION[extension] : undefined;
  if (!type) throw new Error(uiText('Выберите аудиофайл MP3, WAV, M4A, OGG или WebM.'));
  if (file.type === type) return file;
  return new File([file], file.name, { type, lastModified: file.lastModified });
}

/** Whole dataset duration bounds; individual files may be shorter than 2 seconds. */
export function durationProblem(durationMs: number): string | null {
  if (!Number.isFinite(durationMs) || durationMs <= 0) return uiText('Не удалось прочитать аудио');
  if (durationMs < MIN_SAMPLE_MS) return uiText('Образец короче 2 секунд');
  if (durationMs > MAX_SAMPLE_MS) return uiText('Образец длиннее 1,5 минуты');
  return null;
}

/** Reads local metadata only. Cancellation releases the browser decoder and object URL. */
export function readDurationMs(file: File, signal?: AbortSignal): Promise<number> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(new DOMException('Aborted', 'AbortError')); return; }
    const audio = new Audio();
    const url = URL.createObjectURL(file);
    let finished = false;
    const timeout = window.setTimeout(() => finish(null), 10_000);
    const abort = () => finish(null, new DOMException('Aborted', 'AbortError'));
    const finish = (durationMs: number | null, error?: Error) => {
      if (finished) return;
      finished = true;
      window.clearTimeout(timeout); signal?.removeEventListener('abort', abort);
      audio.onloadedmetadata = null; audio.ondurationchange = null; audio.onerror = null;
      try { audio.pause(); audio.removeAttribute('src'); audio.load(); } catch { /* detached decoder */ }
      URL.revokeObjectURL(url);
      if (durationMs == null) reject(error ?? new Error('unreadable'));
      else resolve(durationMs);
    };
    signal?.addEventListener('abort', abort, { once: true });
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      const durationMs = Math.round(audio.duration * 1000);
      if (Number.isFinite(durationMs) && durationMs >= 1) finish(durationMs);
      else if (audio.duration === Infinity) {
        try { audio.currentTime = 1e10; } catch { finish(null); }
      } else finish(null);
    };
    audio.ondurationchange = () => {
      const durationMs = Math.round(audio.duration * 1000);
      if (Number.isFinite(durationMs) && durationMs >= 1) finish(durationMs);
    };
    audio.onerror = () => finish(null);
    try { audio.src = url; } catch { finish(null); }
  });
}

export const sampleFile = { confirmAudioType, durationProblem, readDurationMs };
